/**
 * Cloudinary upload-widget loader.
 *
 * Loads the global widget script on first use, then exposes a thin
 * helper that opens the picker and returns the secure_url of the
 * uploaded image.
 *
 * Cloud name + unsigned preset live here as constants. They're public
 * by design (the cloud name appears in every image URL, and the
 * preset is gated to "unsigned + folder-restricted" inside Cloudinary
 * itself). No API secret ever touches the browser.
 */

export const CLOUDINARY_CLOUD = 'dn2dzxi5z';
export const CLOUDINARY_PRESET = 'Habby POS Banners';

interface CloudinaryWidget {
  open: () => void;
  close: () => void;
  destroy: () => void;
}

interface CloudinaryWidgetResult {
  event: string;
  info: {
    secure_url?: string;
    public_id?: string;
    bytes?: number;
    width?: number;
    height?: number;
    format?: string;
  };
}

type CloudinaryCallback = (error: Error | null, result: CloudinaryWidgetResult) => void;

declare global {
  interface Window {
    cloudinary?: {
      createUploadWidget: (
        opts: Record<string, unknown>,
        cb: CloudinaryCallback,
      ) => CloudinaryWidget;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadScript(): Promise<void> {
  if (typeof window.cloudinary !== 'undefined') return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = 'https://upload-widget.cloudinary.com/global/all.js';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load Cloudinary upload widget'));
    };
    document.body.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Opens the Cloudinary picker. Resolves with the secure URL of the
 * uploaded asset, or rejects on error / cancellation by the user.
 */
export async function pickImageViaCloudinary(opts: { folder?: string } = {}): Promise<string> {
  await loadScript();
  if (!window.cloudinary) throw new Error('Cloudinary widget unavailable');

  return new Promise<string>((resolve, reject) => {
    const widget = window.cloudinary!.createUploadWidget(
      {
        cloudName: CLOUDINARY_CLOUD,
        uploadPreset: CLOUDINARY_PRESET,
        folder: opts.folder,
        sources: ['local', 'camera', 'url'],
        multiple: false,
        clientAllowedFormats: ['png', 'jpg', 'jpeg', 'webp', 'gif'],
        maxImageFileSize: 8_000_000,                       // 8 MB
        cropping: true,
        croppingAspectRatio: 1,                            // 1:1 banner shape
        croppingShowDimensions: true,
        showSkipCropButton: false,
        styles: {
          palette: {
            window: '#FFFFFF',
            sourceBg: '#F8FAFC',
            windowBorder: '#E2E8F0',
            tabIcon: '#0F172A',
            inactiveTabIcon: '#94A3B8',
            menuIcons: '#475569',
            link: '#0D76E1',
            action: '#14B51D',
            inProgress: '#0D76E1',
            complete: '#14B51D',
            error: '#FF3636',
            textDark: '#0F172A',
            textLight: '#FFFFFF',
          },
        },
      },
      (error, result) => {
        if (error) {
          reject(error instanceof Error ? error : new Error(String(error)));
          return;
        }
        if (result?.event === 'success' && result.info?.secure_url) {
          resolve(result.info.secure_url);
          widget.close();
        }
      },
    );
    widget.open();
  });
}
